import { useMemo } from 'react'

import { TwoColumnLayout } from '@factorialco/f0-react'
import { SaleInvoiceDetailDocument } from 'generated/resources/graphql'

import NotFound from 'modules/core/components/NotFound'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import MainContent from './_MainContent'
import SideContent from './_SideContent'

interface SaleInvoiceDetailProps {
  invoiceId: string
  relatedFinancialDocumentPath: (relatedInvoiceId: number) => string
}

const SaleInvoiceDetail = ({ invoiceId, relatedFinancialDocumentPath }: SaleInvoiceDetailProps) => {
  const { access } = useCurrentSession()
  const locale = access.locale

  const { data, error, loading } = useQuery(SaleInvoiceDetailDocument, {
    variables: {
      id: Number(invoiceId),
    },
    fetchPolicy: 'no-cache',
  })

  const saleInvoice = data?.finance?.financialDocument
  const canManageSales = data?.finance?._policies?.financialDocumentsManageSales?.can
  const canSeeComplianceSnapshots =
    data?.finance._policies.complianceSnapshotsSeeComplianceSnapshot.can ?? false
  const complianceExtraFieldsConfigs =
    data?.finance.financialDocumentComplianceExtraFieldsConfigs ?? []
  const complianceSnapshots = data?.finance.complianceSnapshotsConnection?.nodes ?? []
  const canSeeProjects =
    data?.projectManagement?._policies?.sidebarSeeProjectsTopLevelItem?.can ?? false

  const sideContent = useMemo(() => {
    if (!saleInvoice) return null
    return <SideContent saleInvoice={saleInvoice} />
  }, [saleInvoice])

  const project =
    data?.finance?.financialDocument?.projectsFinancialDocumentAssociation?.financialProject

  const relatedProject = project
    ? {
        id: project.id,
        name: project.name,
        canNavigate: canSeeProjects ?? false,
      }
    : undefined

  if (loading) return null

  if (error || !saleInvoice || !canManageSales) return <NotFound />

  return (
    <TwoColumnLayout sideContent={sideContent}>
      <MainContent
        locale={locale}
        saleInvoice={saleInvoice}
        complianceExtraFieldsConfigs={complianceExtraFieldsConfigs}
        relatedFinancialDocumentPath={relatedFinancialDocumentPath}
        relatedProject={relatedProject}
        complianceSnapshots={canSeeComplianceSnapshots ? complianceSnapshots : []}
      />
    </TwoColumnLayout>
  )
}

export default SaleInvoiceDetail
